import Auth from "@aws-amplify/auth";
import React, { createContext } from "react";
import { signIn, signOut } from "utils/auth";

type AuthContextValue = {
  isAuthenticated: boolean;
  userSignIn: (email: string, password: string) => Promise<boolean | undefined>;
  userSignOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth musy be used within AuthContextProvider");
  }

  return context;
};

export const AuthContextProvider: React.FC = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);

  React.useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(() => {
        setIsAuthenticated(true);
      })
      .catch(() => {
        setIsAuthenticated(false);
      });
  }, []);

  const userSignIn = async (email: string, password: string) => {
    const user = await signIn(email, password).catch((e) => {
      switch (e.code) {
        case "UserNotConfirmedException":
          // ユーザのステータスが UNCONFIRMED の場合に起こる。
          // SignUp用のコードを再送し、ステータスを CONFIRMED にする必要がある。
          // 検証コードの再送は １．３節の ResendConfirmationCode() を参照。
          alert("ユーザーが確認されていません。");
          break;
        case "PasswordResetRequiredException":
          // Cognito コンソールでパスワードをリセット（ユーザープールにユーザをインポートする場合も含む）した場合に起こる。
          // パスワードをリセットする必要がある。
          // パスワードのリセットは 3.1節の SendForgotPasswordCode() 参照。
          alert("パスワードを変更してください。");
          break;
        case "NotAuthorizedException":
          // 誤ったパスワードを入力した場合に起こる。
          // 注) パスワードを間違え続けた場合にも起こり、 error.message が 'Password attempts exceeded' になる。
          // （エラーコードとして LimitExceededException が返ってくると思ったらそうではなかった）
          alert("メールアドレスまたはパスワードが存在しません。");
          break;
        case "UserNotFoundException":
          // PASSWORD_VERIFIER は通るものの username が Cognito ユーザープールに存在しない場合に起こる。
          alert("メールアドレスまたはパスワードが存在しません。");
          break;
        case "InvalidParameterException":
          // 入力された username や password が Cognito 側で正しくパースできないとき（バリデーションエラー）に起こる。
          // 注) 2019/04/24 現在、Cognito コンソールでパスワードをリセットした場合は 'PasswordResetRequiredException' ではなくこのエラーコードが返される。
          alert("ユーザーが存在しません。");
          break;
        default:
          // その他のエラー
          console.error(e);
      }
    });
    const signInResult = user !== undefined;
    setIsAuthenticated(signInResult);
    return signInResult;
  };

  const userSignOut = async () => {
    await signOut();
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    userSignIn,
    userSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
